'use client';

import {resourceFromAttributes} from "@opentelemetry/resources";
import {ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION} from "@opentelemetry/semantic-conventions";

import {BatchLogRecordProcessor, LoggerProvider} from "@opentelemetry/sdk-logs";
import {OTLPLogExporter} from "@opentelemetry/exporter-logs-otlp-http";
import {OTEL_SERVICE_NAME} from "@/lib/otlp/shared-services";
import posthog from "posthog-js";
import {context, SpanStatusCode, trace} from "@opentelemetry/api";

type Attributes = Record<string, any>;

export class BrowserLogger {
    private static instance: BrowserLogger | null = null;
    private readonly provider: LoggerProvider | null = null;
    private globalAttributes: Attributes = {};
    private loggerName: string;
    private logger: any; // OTEL logger instance

    private constructor(loggerName: string, defaultAttributes: Attributes = {}) {
        this.loggerName = loggerName;
        this.globalAttributes = defaultAttributes;
        this.provider = this.createProvider();
        this.logger = this.provider?.getLogger(loggerName) ?? this.createNoopLogger();
    }

    // -------------------------
    // 🎯 Singleton Factory
    // -------------------------
    static getInstance(loggerName = "browser-client", defaults: Attributes = {}) {
        if (!BrowserLogger.instance) {
            BrowserLogger.instance = new BrowserLogger(loggerName, defaults);
        }
        return BrowserLogger.instance;
    }

    // -------------------------
    // ⚙ Create OTEL Provider
    // -------------------------
    private createProvider(): LoggerProvider | null {
        if (typeof window === "undefined") return null;

        const resource = resourceFromAttributes({
            [ATTR_SERVICE_NAME]: OTEL_SERVICE_NAME,
            [ATTR_SERVICE_VERSION]: process.env.npm_package_version ?? "1.0.0",
        });

        const exporter = new OTLPLogExporter({
            url: `${window.location.origin}/api/logs`,
        });

        const processor = new BatchLogRecordProcessor(exporter, {
            maxQueueSize: 2000,
            maxExportBatchSize: 50,
            scheduledDelayMillis: 2000,
            exportTimeoutMillis: 15000,
        });

        return new LoggerProvider({
            resource,
            processors: [processor],
        });
    }

    // -------------------------
    // 🧩 No-op logger for SSR
    // -------------------------
    private createNoopLogger() {
        return {
            emit: () => {
            },
        };
    }

    // -------------------------
    // 🌎 Add Global Attributes
    // -------------------------
    addGlobalAttributes(attrs: Attributes) {
        this.globalAttributes = {...this.globalAttributes, ...attrs};
    }

    // -------------------------
    // 📌 Build Attributes + Span Context
    // -------------------------
    private buildAttributes(attrs: Attributes) {
        // const span = trace.getSpan(context.active());
        // const spanCtx = span?.spanContext();

        return {
            ...this.globalAttributes,
            ...attrs,
            // ...(spanCtx && {
            //     trace_id: spanCtx.traceId,
            //     span_id: spanCtx.spanId,
            // }),
        };
    }

    // -------------------------
    // 📝 Core Emit
    // -------------------------
    private emit(severityText: string, message: string, attrs: Attributes = {}) {
        this.logger.emit({
            severityText,
            body: message,
            attributes: this.buildAttributes(attrs),
        });
    }

    // -------------------------
    // 🔊 Public Methods
    // -------------------------
    info(message: string, attributes: Attributes = {}) {
        console.log("[INFO]", message, attributes);
        this.emit("INFO", message, attributes);
    }

    debug(message: string, attributes: Attributes = {}) {
        this.emit("DEBUG", message, attributes);
    }

    warn(message: string, attributes: Attributes = {}) {
        this.emit("WARN", message, attributes);
    }

    trace(message: string, attributes: Attributes = {}) {
        this.emit("TRACE", message, attributes);
    }

    error(message: string, error: Error, attributes: Attributes = {}) {
        console.error("[ERROR]", message, error);
        const span = trace.getSpan(context.active());
        posthog.captureException(error, {
            message,
            ...attributes,
        });
        span?.recordException(error)
        span?.setStatus({code: SpanStatusCode.ERROR, message: String(error)});

        this.emit("ERROR", message, {
            ...attributes,
            error_name: error.name,
            error_message: error.message,
            error_stack: error.stack ?? "no-stack",
        });
    }

    // -------------------------
    //  Get Raw OTEL Logger Provider
    // -------------------------
    getProvider() {
        return this.provider;
    }
}

// -------------------------
//  Global Singleton Browser Logger
// -------------------------
export const browserLog = BrowserLogger.getInstance("interview-console-client", {
    env: process.env.NODE_ENV,
    platform: "Interview Console",
});

// Remember it Abhinandan
// Currently if log is called in outside trace it don't automatically add themself to active trace means it doesn't show in traces